var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { KEYS } from '../../util/event-keys';
import { announce } from '../../util/LiveAnnouncer';
import { BlocksElement } from '../BlocksElement';
import styles from './table-body.styles';
/**
 * @slot default - The table body content
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default slot.
 */
export class BlocksTableBody extends BlocksElement {
    static { this.styles = styles; }
    /**
     * Query all the rows in the cat table
     */
    get _catTableRows() {
        return Array.from(this.children);
    }
    constructor() {
        super();
        /**
         * Delay for announcement like "please enter space to drop"
         */
        this.dropAnnouncementDelay = 3000;
        /**
         * Text to announce when a row is dragged
         */
        this.rowDraggedText = 'Row dragged';
        /**
         * Text to announce when a row is dropped
         */
        this.rowDroppedText = 'Row dropped';
        /**
         * Text template to announce when a row is to be inserted between two specified rows.
         * Specify a template such as "Insert between row {prevRow} and {nextRow}". The placeholders will be
         * replaced with actual row numbers during screen reader announcements.
         */
        this.insertBetweenText = 'Insert between row {prevRow} and {nextRow}';
        /**
         * Text template to announce when a row is to be inserted after first row.
         * Specify a template such as "Insert before row {nextRow}". The placeholders will be
         * replaced with actual row numbers during screen reader announcements.
         */
        this.insertBeforeText = 'Insert before row {nextRow}';
        /**
         * Text template to announce when a row is to be inserted before last row.
         * Specify a template such as "Insert after row {prevRow}". The placeholders will be
         * replaced with actual row numbers during screen reader announcements.
         */
        this.insertAfterText = 'Insert after row {prevRow}';
        /**
         * Text to announce prompting the user to press space or enter to drop the row
         */
        this.clickToDropText = 'press enter/space to drop the row';
        this.isDraggingInProgress = false;
        this.arrowDirection = null;
        this.timeoutId = null;
        this.dragRow = null;
        this.handleDragStart = (e) => {
            const { dragRow } = e.detail;
            this.isDraggingInProgress = true;
            this.dragRow = dragRow;
            // announces row dragged text
            this.dragRow.dragAnnouncementText = this.rowDraggedText;
            // announces in between rows as well after a slight delay
            setTimeout(() => {
                this.announce(dragRow);
            }, 300);
        };
        this.handleDragEnd = async (e) => {
            e.preventDefault();
            const target = e.target;
            // since start and end are same element
            if (target.tagName === 'CAT-TABLE-CELL') {
                return;
            }
            this.dispatch({
                eventName: 'bl-keyboard-drag-end',
                detailObj: { row: target, direction: this.arrowDirection }
            });
            this.timeoutId = null;
            this.isDraggingInProgress = false;
            this.arrowDirection = null;
            announce(this.rowDroppedText);
            await this.updateComplete;
            setTimeout(() => {
                const rowElement = target?.shadowRoot?.querySelector('.cat-c-table-row');
                const rowNumber = Number(rowElement.getAttribute('data-row'));
                // In angular tab index is getting reset on table body rerendering. Hence picking row with index value.
                const targetRow = this._catTableRows[rowNumber - 1];
                const targetElement = targetRow?.shadowRoot?.querySelector('.cat-c-table-row');
                targetElement?.setAttribute('tabindex', '-1');
                targetElement?.focus();
            }, 100);
        };
        this.handleDragReset = () => {
            // if focus lost during the drag process then reset required
            if (this.isDraggingInProgress) {
                this.dispatch({
                    eventName: 'bl-keyboard-drag-reset'
                });
            }
        };
        this.getDeepActiveElement = () => {
            let activeElement = document.activeElement;
            while (activeElement?.shadowRoot) {
                activeElement = activeElement.shadowRoot.activeElement || activeElement;
            }
            return activeElement;
        };
        this.makeAnnouncement = (e) => {
            e.preventDefault();
            let currentActiveRow = null;
            let currentIndex;
            const currentActiveElement = this.getDeepActiveElement();
            const currentActiveRowNode = currentActiveElement?.getRootNode();
            if (currentActiveRowNode instanceof ShadowRoot) {
                currentActiveRow = currentActiveRowNode.host;
            }
            const children = Array.from(this.children);
            if (currentActiveRow) {
                currentIndex = children.indexOf(currentActiveRow);
                if (e.key === KEYS.DOWN_ARROW && currentIndex < children.length - 1) {
                    this.arrowDirection = 'after';
                    const row = children[currentIndex + 1];
                    this.setFocusAndAnnounce(row);
                }
                else if (e.key === KEYS.UP_ARROW && currentIndex > 0) {
                    this.arrowDirection = 'before';
                    const row = children[currentIndex - 1];
                    this.setFocusAndAnnounce(row);
                }
            }
        };
        this.handleOnEscape = () => {
            const currentActiveElement = document?.activeElement?.shadowRoot?.activeElement;
            const firstTablecell = currentActiveElement.children[0];
            const btn = firstTablecell.shadowRoot?.querySelector('.cat-c-table-cell__draggable-button');
            btn?.focus();
            this.dispatch({
                eventName: 'bl-keyboard-drag-reset'
            });
            this.isDraggingInProgress = false;
        };
        this.handleKeyDown = (e) => {
            if (this.isDraggingInProgress && (e.key === KEYS.ENTER || e.key === KEYS.SPACE)) {
                this.handleDragEnd(e);
            }
            if (this.isDraggingInProgress) {
                this.makeAnnouncement(e);
            }
            if (this.isDraggingInProgress && e.key === KEYS.ESCAPE) {
                this.handleOnEscape();
            }
        };
        this.getRowNumber = (row) => {
            if (row) {
                const rowElement = row?.shadowRoot?.querySelector('.cat-c-table-row');
                const rowNumberByIndex = this._catTableRows.indexOf(row) + 1;
                return rowElement.getAttribute('data-row') ?? rowNumberByIndex;
            }
            return '';
        };
        this.setFocusAndAnnounce = (row) => {
            const rowElement = row?.shadowRoot?.querySelector('.cat-c-table-row');
            this.announce(row);
            rowElement.setAttribute('tabindex', '-1');
            setTimeout(() => {
                rowElement?.focus();
            }, 0);
        };
        this.updateRowPositions = () => {
            const rows = Array.from(this.children);
            rows.forEach((row, index) => {
                const rowElement = row?.shadowRoot?.querySelector('.cat-c-table-row');
                if (rowElement) {
                    rowElement.setAttribute('data-row', `${index + 1}`);
                }
            });
        };
        this.announce = (row, text = '') => {
            let announcementText = text;
            if (this._catTableRows) {
                const firsRow = this._catTableRows[0];
                const lastRow = this._catTableRows[this._catTableRows.length - 1];
                const prevRow = row.previousElementSibling;
                const nextRow = row.nextElementSibling;
                const prevRowNumber = this.getRowNumber(prevRow);
                const nextRowNumber = this.getRowNumber(nextRow);
                if (row.isEqualNode(firsRow)) {
                    announcementText = this.insertBeforeText.replace('{nextRow}', nextRowNumber.toString());
                }
                else if (row.isEqualNode(lastRow)) {
                    announcementText = this.insertAfterText.replace('{prevRow}', prevRowNumber.toString());
                }
                else {
                    if (this.timeoutId) {
                        clearTimeout(this.timeoutId);
                    }
                    announcementText = this.insertBetweenText.replace('{prevRow}', prevRowNumber.toString()).replace('{nextRow}', nextRowNumber.toString());
                    // announces after delay
                    this.timeoutId = setTimeout(() => {
                        if (this.isDraggingInProgress) {
                            row.dragAnnouncementText = this.clickToDropText;
                        }
                    }, this.dropAnnouncementDelay);
                }
            }
            row.dragAnnouncementText = announcementText;
        };
        this.addEventListener('bl-keyboard-drag-start', this.handleDragStart);
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('mousedown', this.handleDragReset);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('mousedown', this.handleDragReset);
    }
    firstUpdated() {
        // announce earlier to create aria live region placeholder
        announce('');
        this.updateRowPositions();
    }
    render() {
        const componentClassName = this.componentClassNames('cat-c-table-body', {});
        return html `
      <div part="base" role="rowgroup" class="${componentClassName}" @keydown="${this.handleKeyDown}">
        <slot
          @slotchange=${() => {
            this.updateRowPositions();
        }}
          part="part-default"
        ></slot>
      </div>
    `;
    }
}
__decorate([
    property({ type: Number })
], BlocksTableBody.prototype, "dropAnnouncementDelay", void 0);
__decorate([
    property({ type: String })
], BlocksTableBody.prototype, "rowDraggedText", void 0);
__decorate([
    property({ type: String })
], BlocksTableBody.prototype, "rowDroppedText", void 0);
__decorate([
    property({ type: String })
], BlocksTableBody.prototype, "insertBetweenText", void 0);
__decorate([
    property({ type: String })
], BlocksTableBody.prototype, "insertBeforeText", void 0);
__decorate([
    property({ type: String })
], BlocksTableBody.prototype, "insertAfterText", void 0);
__decorate([
    property({ type: String })
], BlocksTableBody.prototype, "clickToDropText", void 0);
if (customElements.get('cat-table-body') === undefined) {
    customElements.define('cat-table-body', BlocksTableBody);
}
